import * as THREE from "three";
import { useEffect, useRef, useState } from "react";
import { easing } from "maath";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Sky, Bvh } from "@react-three/drei";
import {
  Bloom,
  DepthOfField,
  EffectComposer,
  Outline,
  N8AO,
  TiltShift2,
  ToneMapping,
  SSAO,
  SMAA,
} from "@react-three/postprocessing";
import { BlendFunction, OverrideMaterialManager } from "postprocessing";
import { useProjectFocus } from "@/stores/projectFocusStore";

// Outline (and other passes that render with an override material) ignore
// bone/morph transforms by default, so skinned meshes like the Cria llama
// get their outline drawn at bind pose. The workaround clones the override
// material per object with the right skinning/morph flags.
OverrideMaterialManager.workaroundEnabled = true;

export default function Effects() {
  const { size } = useThree();
  // Depth-of-field for the focus view. The effect stays mounted after the
  // first focus (remounting rebuilds the whole composer - a shader-compile
  // stall right at the transition) and is driven imperatively instead:
  // target follows the focused model, bokeh eases to 0 when unfocused.
  const focused = useProjectFocus((s) => Boolean(s.focused));
  // Bumped on every focus entry: the DepthOfField instance must be
  // recreated per session, because the composer rebuilds on camera swaps
  // and a persisted effect keeps a stale depth-buffer wiring (state looks
  // right but nothing blurs). Shaders are warm after the first compile, so
  // the per-session rebuild is cheap.
  const [dofSession, setDofSession] = useState(0);
  useEffect(() => {
    if (focused) setDofSession((n) => n + 1);
  }, [focused]);
  const dofRef = useRef();
  useFrame((_, delta) => {
    const effect = dofRef.current;
    if (!effect) return;
    const f = useProjectFocus.getState().focused;
    if (f) {
      // target starts as null on the effect; give it a vector once.
      if (!effect.target) effect.target = new THREE.Vector3();
      effect.target.fromArray(f.center);
    }
    effect.bokehScale = THREE.MathUtils.damp(
      effect.bokehScale,
      f ? 6 : 0,
      6,
      Math.min(delta, 0.05),
    );
  });
  const fxParams =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search)
      : new URLSearchParams();
  if (fxParams.get("fx") === "off") return null;
  // useFrame((state, delta) => {
  // easing.damp3(
  //   state.camera.position,
  //   [20 + state.pointer.x / 2, 20 + state.pointer.y / 4, 20],
  //   0.2,
  //   delta,
  // );
  //   easing.damp3(
  //     state.camera.position,
  //     [
  //       state.pointer.x,
  //       1 + state.pointer.y / 2,
  //       8 + Math.atan(state.pointer.x * 2),
  //     ],
  //     0.3,
  //     delta,
  //   );
  //   state.camera.lookAt(state.camera.position.x * 0.9, 0, -4);
  // });
  return (
    <EffectComposer
      stencilBuffer
      disableNormalPass
      autoClear={false}
      // MSAA 4 + N8AO forced a multisampled-buffer resolve every frame and
      // cost ~4ms (50fps with 33ms spikes on a desktop GPU). SMAA as the
      // first pass gives comparable edges at a fraction of the cost.
      // NOTE: SMAA must run BEFORE Bloom; appended last it clamps the HDR
      // buffer and kills the bloom glow.
      multisampling={0}
    >
      {/* //// FLAT Effects */}
      <SMAA />
      {dofSession > 0 && (
        <DepthOfField
          key={dofSession}
          ref={dofRef}
          worldFocusRange={12}
          bokehScale={0}
        />
      )}
      <Bloom
        mipmapBlur
        luminanceThreshold={1.3}
        intensity={1.42}
        radius={0.72}
      />
      <ToneMapping
        blendFunction={BlendFunction.SCREEN} // blend mode
        adaptive={true} // toggle adaptive luminance map usage
        resolution={256} // texture resolution of the luminance map
        middleGrey={0.6} // middle grey factor
        maxLuminance={16.0} // maximum luminance
        averageLuminance={1.0} // average luminance
        adaptationRate={1.0} // luminance adaptation rate
      />
      {/* <Outline
        visibleEdgeColor="white"
        hiddenEdgeColor="white"
        blur
        width={size.width * 1.25}
        edgeStrength={10}
      /> */}
      {fxParams.get("outline") !== "off" && (
        <Outline blur visibleEdgeColor="white" edgeStrength={100} width={1000} />
      )}
      {/* <TiltShift2 samples={5} blur={0.1} /> */}
      {/* //// FLAT Effects */}

      {/* //// FAKE Shadows - Ambient Occlusions Effects */}
      {/* <SSAO
        radius={0.05}
        intensity={150}
        luminanceInfluence={0.5}
        color="black"
      /> */}
      {fxParams.get("ao") !== "off" && (
        <N8AO
          aoRadius={50}
          distanceFalloff={0.2}
          intensity={4}
          screenSpaceRadius
          halfRes
        />
      )}
      {/* //// FAKE Shadows - Ambient Occlusions Effects */}
    </EffectComposer>
  );
}
